/**
 * FlashcardManagementPage — coach portal page for managing flashcard scenes.
 *
 * Route: /coach/flashcards
 *
 * Features:
 * - Lists all flashcard scenes by domain (uses GET /coach/flashcards)
 * - Shows module_id association for each card
 * - Allows inline editing of question / answer / key_insight
 * - Shows how many learners have each card in their queue (learner_count)
 * - Allows manually assigning cards for a module to a specific learner
 * - Create new flashcards tied to a module
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CoachLayout from './CoachLayout';
import useOrgScope, { expandAliases } from '../../hooks/useOrgScope';
import { curriculumAPI } from '../../services/api';

const API_BASE = '/scenario-os';
const TOKEN_KEY = 'scenario_os_token';

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
}
function authHeaders() {
  const t = getToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

const DIFF_COLORS = {
  beginner: '#4ade80',
  intermediate: '#fbbf24',
  advanced: '#f87171',
};

function EditModal({ card, onSave, onClose }) {
  const [form, setForm] = useState({
    question: card.question || '',
    answer: card.answer || '',
    key_insight: card.key_insight || '',
    difficulty: card.difficulty || 'beginner',
    module_id: card.module_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const sceneId = card.scene_id || card.skill_id;
      await apiFetch(`/coach/flashcards/${sceneId}`, {
        method: 'PUT',
        body: JSON.stringify({
          question: form.question || undefined,
          answer: form.answer || undefined,
          key_insight: form.key_insight || undefined,
          difficulty: form.difficulty || undefined,
          module_id: form.module_id || undefined,
        }),
      });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8, color: '#e2e8f0', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
          padding: 24, width: 560, maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <h3 style={{ color: '#e2e8f0', margin: '0 0 16px' }}>Edit Flashcard</h3>
        <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16 }}>
          {card.scene_id || card.skill_id} · {card.domain_id}
        </div>

        {[
          { label: 'Question (front)', key: 'question', rows: 3 },
          { label: 'Answer (back)', key: 'answer', rows: 4 },
          { label: 'Key Insight', key: 'key_insight', rows: 2 },
        ].map(({ label, key, rows }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>{label}</label>
            <textarea
              value={form[key]}
              onChange={set(key)}
              rows={rows}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Difficulty</label>
            <select value={form.difficulty} onChange={set('difficulty')} style={inputStyle}>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Module ID</label>
            <input value={form.module_id} onChange={set('module_id')} style={inputStyle} placeholder="e.g. ai_orch_w3" />
          </div>
        </div>

        {error && (
          <div style={{ color: '#f87171', background: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ background: '#6366f1', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ domain, domainOptions = [], onSave, onClose }) {
  const [form, setForm] = useState({
    domain_id: domain || domainOptions[0]?.id || 'ai',
    module_id: '',
    question: '',
    answer: '',
    key_insight: '',
    difficulty: 'beginner',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleCreate = async () => {
    if (!form.module_id.trim() || !form.question.trim() || !form.answer.trim() || !form.key_insight.trim()) {
      setError('Module ID, question, answer, and key insight are all required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/coach/flashcards', { method: 'POST', body: JSON.stringify(form) });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8, color: '#e2e8f0', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 24, width: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ color: '#e2e8f0', margin: '0 0 16px' }}>New Flashcard</h3>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Domain *</label>
            <select value={form.domain_id} onChange={set('domain_id')} style={inputStyle}>
              {domainOptions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Module ID *</label>
            <input value={form.module_id} onChange={set('module_id')} style={inputStyle} placeholder="ai_orch_w3" />
          </div>
        </div>

        {[
          { label: 'Question (front) *', key: 'question', rows: 3 },
          { label: 'Answer (back — explain WHY) *', key: 'answer', rows: 4 },
          { label: 'Key Insight (underlying principle) *', key: 'key_insight', rows: 2 },
        ].map(({ label, key, rows }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>{label}</label>
            <textarea value={form[key]} onChange={set(key)} rows={rows} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        ))}

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Difficulty</label>
          <select value={form.difficulty} onChange={set('difficulty')} style={inputStyle}>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {error && <div style={{ color: '#f87171', background: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleCreate} disabled={saving} style={{ background: '#6366f1', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Creating…' : 'Create Flashcard'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ domainOptions = [], onClose }) {
  const [form, setForm] = useState({
    learner_id: '',
    module_id: '',
    domain: domainOptions[0]?.id || 'ai',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleAssign = async () => {
    if (!form.learner_id.trim() || !form.module_id.trim()) {
      setError('Learner ID and Module ID are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch('/adaptive/assign-flashcards', { method: 'POST', body: JSON.stringify(form) });
      setResult(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 24, width: 440 }}>
        <h3 style={{ color: '#e2e8f0', margin: '0 0 4px' }}>Assign Flashcards to Learner</h3>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>Manually assign all flashcards for a module to a learner's review queue.</p>

        {['learner_id', 'module_id'].map((key) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>{key === 'learner_id' ? 'Learner ID' : 'Module ID'} *</label>
            <input value={form[key]} onChange={set(key)} style={inputStyle} placeholder={key === 'learner_id' ? 'learner_demo1' : 'ai_orch_w3'} />
          </div>
        ))}

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Domain *</label>
          <select value={form.domain} onChange={set('domain')} style={inputStyle}>
            {domainOptions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>

        {result && (
          <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#4ade80', fontSize: 13 }}>
            Assigned {result.assigned} new flashcards to {result.learner_id}
          </div>
        )}
        {error && <div style={{ color: '#f87171', background: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>Close</button>
          <button onClick={handleAssign} disabled={loading} style={{ background: '#6366f1', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FlashcardManagementPage() {
  const { domains: scopeDomains, unrestricted, loading: scopeLoading } = useOrgScope();
  const [catalogDomains, setCatalogDomains] = useState([]);
  const [domain, setDomain] = useState('ai');
  const [moduleFilter, setModuleFilter] = useState('');
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => {
    if (!unrestricted) return undefined;
    let cancelled = false;
    curriculumAPI.listDomains()
      .then(({ data }) => {
        if (cancelled) return;
        setCatalogDomains((data?.domains || []).map((d) => ({
          id: d.domain_id,
          label: d.display_name || d.domain_id,
        })));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [unrestricted]);

  const domainOptions = useMemo(() => {
    if (scopeDomains?.length) {
      return scopeDomains.map((d) => ({
        id: d.domain_id,
        label: d.display_name || d.domain_id,
      }));
    }
    if (unrestricted && catalogDomains.length) return catalogDomains;
    return [{ id: 'ai', label: 'AI' }];
  }, [scopeDomains, unrestricted, catalogDomains]);

  useEffect(() => {
    if (scopeLoading || !domainOptions.length) return;
    if (!domainOptions.some((d) => d.id === domain)) {
      setDomain(domainOptions[0].id);
    }
  }, [scopeLoading, domainOptions, domain]);

  const load = useCallback(async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    try {
      // Query canonical domain; also try AI aliases for legacy flashcard rows
      const aliases = [...expandAliases([domain])];
      const seen = new Set();
      const cards = [];
      for (const d of aliases) {
        const params = new URLSearchParams({ domain: d });
        if (moduleFilter.trim()) params.set('module_id', moduleFilter.trim());
        try {
          const data = await apiFetch(`/coach/flashcards?${params}`);
          for (const c of data.flashcards || []) {
            const key = c.scene_id || c.skill_id || JSON.stringify(c);
            if (seen.has(key)) continue;
            seen.add(key);
            cards.push(c);
          }
        } catch (_) {
          /* domain may have no flashcards */
        }
      }
      setFlashcards(cards);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [domain, moduleFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = flashcards.reduce((acc, card) => {
    const key = card.module_id || '(no module)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(card);
    return acc;
  }, {});

  const inputStyle = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e2e8f0', padding: '7px 12px', fontSize: 13 };

  return (
    <CoachLayout title="Flashcard Management">
      {editCard && <EditModal card={editCard} onSave={() => { setEditCard(null); load(); }} onClose={() => setEditCard(null)} />}
      {showCreate && (
        <CreateModal
          domain={domain}
          domainOptions={domainOptions}
          onSave={() => { setShowCreate(false); load(); }}
          onClose={() => setShowCreate(false)}
        />
      )}
      {showAssign && (
        <AssignModal domainOptions={domainOptions} onClose={() => setShowAssign(false)} />
      )}

      <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, color: '#e2e8f0', fontSize: 22, fontWeight: 700 }}>Flashcard Management</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              Manage flashcard scenes by domain and module. Cards are assigned to learners when they complete the associated module.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setShowAssign(true)}
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}
            >
              Assign to Learner
            </button>
            <button
              onClick={() => setShowCreate(true)}
              style={{ background: '#6366f1', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + New Flashcard
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} style={inputStyle}>
            {domainOptions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <input
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            placeholder="Filter by module_id…"
            style={{ ...inputStyle, width: 220 }}
          />
          <span style={{ color: '#64748b', fontSize: 13 }}>{flashcards.length} cards</span>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', color: '#f87171', marginBottom: 20 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>Loading flashcards…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div style={{ color: '#64748b', textAlign: 'center', padding: 40, background: 'rgba(255,255,255,0.03)', borderRadius: 12 }}>
            No flashcards found for {domain}
            {moduleFilter ? ` / module ${moduleFilter}` : ''}.
          </div>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([modId, cards]) => (
            <div key={modId} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Module: {modId}
                </span>
                <span style={{ color: '#475569', fontSize: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: '2px 8px' }}>
                  {cards.length} cards
                </span>
              </div>

              {cards.map((card) => {
                const sid = card.scene_id || card.skill_id;
                const diffColor = DIFF_COLORS[card.difficulty] || '#94a3b8';
                return (
                  <div
                    key={sid}
                    style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10, padding: '14px 16px', marginBottom: 10,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ color: '#475569', fontSize: 11, fontFamily: 'monospace' }}>{sid}</span>
                          <span style={{ fontSize: 11, color: diffColor, background: `${diffColor}22`, borderRadius: 4, padding: '1px 7px' }}>
                            {card.difficulty || 'beginner'}
                          </span>
                          {card.learner_count > 0 && (
                            <span style={{ fontSize: 11, color: '#6366f1', background: 'rgba(99,102,241,0.15)', borderRadius: 4, padding: '1px 7px' }}>
                              {card.learner_count} learner{card.learner_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                          Q: {card.question}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 4 }}>
                          A: {card.answer?.slice(0, 160)}{(card.answer?.length || 0) > 160 ? '…' : ''}
                        </div>
                        {card.key_insight && (
                          <div style={{ color: '#6366f1', fontSize: 12, fontStyle: 'italic' }}>
                            Insight: {card.key_insight}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setEditCard(card)}
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </CoachLayout>
  );
}
